import React, { useEffect, useState } from "react";
import Lottie from "lottie-react";
import { css } from "@emotion/react";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import loading from "@src/assets/Loading.json";
import {
    useFindSubsInfo,
    useFindSubsRefundAmount,
    useRemoveUserSubs,
    useRemoveUserSubsPayDeposit,
    useRemoveUserSubsReserve,
} from "@src/hooks/subscribe/useSubscribe";
import { getDeviceType, transferToNative } from "@src/lib/WebAppBridge";
import ConfirmModal from "@src/components/popup/ConfirmModal";
import sadJson from "@src/assets/unSubscription/sad.json";
import useCallToken from "@src/hooks/useCallToken";
import styled from "styled-components";
import TerminateFirstStep from "./TerminateFirstStep";
import TerminateNow from "./TerminateNow";
import MgDialog from "@src/components/Dialog";
import { useNavigate } from "react-router-dom";

interface Props {}

const UnSubscribe: React.FC<Props> = () => {
    const { token } = useCallToken();
    const navigate = useNavigate();
    const [step, setStep] = useState<number>(1); // 1 첫화면 2 해지 3 해지예약

    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [showUsedStateLand, setShowUsedStateLand] = useState<boolean>(false);
    const [isOpenImmediateTerminateNoneDeposit, setIsOpenImmediateTerminateNoneDeposit] = useState<boolean>(false);
    const [isOpenReservationModal, setIsOpenReservationModal] = useState<boolean>(false);
    const [isOpenImmediateTerminateModal, setIsOpenImmediateTerminateModal] = useState<boolean>(false);
    const [isOpenImmediateTerminateUseUntilEndModal, setIsOpenImmediateTerminateUseUntilEndModal] = useState<boolean>(false);
    const [isOpenInAppErrorModal, setIsOpenInAppErrorModal] = useState<boolean>(false);
    const [isOpenPayDepositModal, setIsOpenPayDepositModal] = useState<boolean>(false);

    // 즉시 해지시 환불액
    const { data: subsRefundAmount, refetch: refetchSubsRefundAmount } = useFindSubsRefundAmount();

    // 유저 구독 정보
    const { data: userSubsInfo, refetch: refetchSubsInfo, isLoading: isLoadingSubsInfo } = useFindSubsInfo();

    useEffect(() => {
        if (token.length) {
            refetchSubsInfo();
            refetchSubsRefundAmount();
        }
    }, [token]);

    //* 유저 구독 즉시 해지
    const removeSubs = useRemoveUserSubs(
        (res) => {
            if (res.status === 200) {
                transferToNative(false, "closeUnsubscribeView");
            }
            if (res.status === 500) {
                return setIsOpenInAppErrorModal(true);
            }
        },
        (err) => {
            const newErr: any = err?.response?.data;
            setErrorMessage(newErr?.message);
            setShowErrorModal(true);
        },
    );

    //* 유저 구독 즉시 해지
    const removeSubsNoneDeposit = useRemoveUserSubs(
        (res) => {
            if (res.status === 200) {
                transferToNative(false, "onClickRedirectSubscribeView");
            }
            if (res.status === 500) {
                return setIsOpenInAppErrorModal(true);
            }
        },
        (err) => {
            const newErr: any = err?.response?.data;
            setErrorMessage(newErr?.message);
            setShowErrorModal(true);
        },
    );

    //* 유저 구독 즉시 해지 (월 결제 위약금이 있을때)
    const monthlyUserSubscriptionRemove = useRemoveUserSubsPayDeposit(
        (res) => {
            if (res.status === 200) {
                setIsOpenImmediateTerminateModal(false);
                setIsOpenImmediateTerminateUseUntilEndModal(true);
            }
            if (res.status === 500) {
                return setIsOpenInAppErrorModal(true);
            }
        },
        (err) => {
            const newErr: any = err?.response?.data;
            setErrorMessage(newErr?.message);
            setShowErrorModal(true);
        },
    );

    const userSubscriptionRemoveReserve = useRemoveUserSubsReserve(
        () => {
            transferToNative(false, "closeUnsubscribeView");
        },
        (err) => {
            const newErr: any = err?.response?.data;
            setErrorMessage(newErr?.message);
            setShowErrorModal(true);
        },
    );

    return (
        <>
            <div css={rootBoxStyle}>
                <div css={headStyle}>
                    <ArrowBackIosNewRoundedIcon
                        onClick={() => {
                            if (step === 2) {
                                return setStep(1);
                            }
                            return transferToNative(false, "closeUnsubscribeView");
                        }}
                    />
                    <p className="name">{step === 1 ? "해지하기" : step === 2 ? "즉시 해지하기" : "해지 예약하기"}</p>
                    <p />
                </div>

                <div css={contentStyle}>
                    {isLoadingSubsInfo || !userSubsInfo ? (
                        <div css={lottieBoxStyle}>
                            <Lottie className="lottie" animationData={loading} />
                        </div>
                    ) : (
                        <>
                            {/* 유지, 해지, 예약 선택 화면 */}
                            {step === 1 && (
                                <div style={{ height: "calc(100vh - 88px)" }}>
                                    <LottieBoxStyle>
                                        <Lottie className="lottie" animationData={sadJson} />
                                    </LottieBoxStyle>
                                    <TerminateFirstStep
                                        userSubsInfo={userSubsInfo}
                                        subsRefundAmount={subsRefundAmount}
                                        onChangeStep={(val: number) => {
                                            setStep(val);
                                        }}
                                        onCloseView={() => transferToNative(false, "closeUnsubscribeView")}
                                        setIsOpenImmediateTerminateNoneDeposit={() =>
                                            setIsOpenImmediateTerminateNoneDeposit(true)
                                        }
                                        onSubmitUserSubsRemoveReserve={() => setIsOpenReservationModal(true)}
                                        setIsOpenReservationModal={() => setIsOpenReservationModal(true)}
                                    />
                                </div>
                            )}

                            {/* 즉시 해지 */}
                            {step === 2 && (
                                <TerminateNow
                                    subsRefundAmount={subsRefundAmount}
                                    userSubsInfo={userSubsInfo}
                                    onCloseView={() => transferToNative(false, "closeUnsubscribeView")}
                                    onClickUnsubs={() => {
                                        if (
                                            new Date(subsRefundAmount?.firstPaidAt ?? 0) >= new Date("2023-10-23") &&
                                            new Date(subsRefundAmount?.firstPaidAt ?? 0) < new Date("2023-11-14") &&
                                            (subsRefundAmount?.usedDays ?? 0) >= 8
                                        ) {
                                            return;
                                        }
                                        refetchSubsInfo?.();

                                        if (subsRefundAmount?.penaltyType === 5) {
                                            setIsOpenPayDepositModal(true);
                                            return;
                                        }
                                        setIsOpenImmediateTerminateModal(true);
                                    }}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* 즉시 해지 위약금 없을 때 모달 */}
            {isOpenImmediateTerminateNoneDeposit && (
                <MgDialog
                    open={isOpenImmediateTerminateNoneDeposit}
                    onCloseView={() => setIsOpenImmediateTerminateNoneDeposit(false)}
                    contents={
                        <ImmediateTerminateNoneDepositModal>
                            <div className="Title">해지하기</div>
                            <div className="SubTitle">
                                해지 이후에는 더 이상 유료 서비스 <br />
                                이용이 불가합니다
                            </div>
                        </ImmediateTerminateNoneDepositModal>
                    }
                    width="284px"
                    confirmStyle={ConfirmStyle}
                    cancelStyle={ConfirmStyle}
                    cancelText="취소"
                    onClickSubmit={() => {
                        removeSubsNoneDeposit.mutate();
                    }}
                    confirmText="해지하기"
                    useOnCloseView={false}
                />
            )}

            {/* 즉시 해지 위약금 있을 때 모달 */}
            {isOpenImmediateTerminateModal && (
                <MgDialog
                    open={isOpenImmediateTerminateModal}
                    onCloseView={() => setIsOpenImmediateTerminateModal(false)}
                    contents={
                        <ModalContentStyle>
                            <p className="titleText">구독 환불하기</p>
                            <p className="titleDescText">환불금액을 확인해주세요</p>
                            <div className="priceText">
                                <p>구독 결제금액</p>
                                <p>{subsRefundAmount?.paidPrice?.toLocaleString()}원</p>
                            </div>
                            {subsRefundAmount?.penaltyType === 4 && (
                                <>
                                    <div className="priceText">
                                        <p>취소 위약금(정가 20%)</p>
                                        <p>{(subsRefundAmount?.penaltyPrice * -1)?.toLocaleString()}원</p>
                                    </div>
                                    <div className="priceText usedText">
                                        <p>구독 사용금액</p>
                                        <p>{(subsRefundAmount?.usedDaysPrice * -1)?.toLocaleString()}원</p>
                                    </div>
                                </>
                            )}
                            {subsRefundAmount?.penaltyType === 5 && (
                                <>
                                    <div className="priceText usedText">
                                        <p>위약금</p>
                                        <p>{subsRefundAmount?.penaltyPrice?.toLocaleString()}원</p>
                                    </div>
                                    <div className="refundText">
                                        <p>총 공제금액</p>
                                        <p>
                                            {((subsRefundAmount?.paidPrice ?? 0) - (subsRefundAmount?.penaltyPrice ?? 0) < 0
                                                ? 0
                                                : (subsRefundAmount?.paidPrice ?? 0) - (subsRefundAmount?.penaltyPrice ?? 0)
                                            )?.toLocaleString()}
                                            원
                                        </p>
                                    </div>
                                </>
                            )}
                            <div className="refundText">
                                <p>총 환불금액</p>
                                <p>
                                    {((subsRefundAmount?.penaltyPrice ?? 0) + (subsRefundAmount?.usedDaysPrice ?? 0)) * -1 > 0
                                        ? (
                                              ((subsRefundAmount?.penaltyPrice ?? 0) + (subsRefundAmount?.usedDaysPrice ?? 0)) *
                                              -1
                                          )?.toLocaleString()
                                        : 0}
                                    원
                                </p>
                            </div>
                            {subsRefundAmount?.penaltyType === 5 && (
                                <div className="refundWarningText">
                                    총 {(subsRefundAmount?.penaltyPrice - subsRefundAmount?.paidPrice).toLocaleString()}
                                    원을 지급해야 <br />
                                    정상 해지됩니다.
                                </div>
                            )}
                        </ModalContentStyle>
                    }
                    width="284px"
                    confirmStyle={ConfirmStyle}
                    cancelStyle={ConfirmStyle}
                    confirmText="해지하기"
                    cancelText="취소"
                    onClickSubmit={() => {
                        if (subsRefundAmount?.penaltyType === 5) {
                            setIsOpenPayDepositModal(true);
                        } else {
                            removeSubsNoneDeposit.mutate();
                        }
                    }}
                    useOnCloseView={false}
                />
            )}

            {/* 즉시 후 이달까지 사용가능 안내 모달 */}
            {isOpenImmediateTerminateUseUntilEndModal && (
                <MgDialog
                    open={isOpenImmediateTerminateUseUntilEndModal}
                    onCloseView={() => {
                        transferToNative("", "closeUnsubscribeView");
                        setIsOpenImmediateTerminateUseUntilEndModal(false);
                    }}
                    contents={
                        <ImmediateTerminateNoneDepositModal>
                            <div className="Title">해지하기</div>
                            <div className="SubTitle">
                                결제하신 카드로 위약금이 인출되었으며, <br />
                                해달 월까지 서비스 이용이 가능합니다.
                            </div>
                        </ImmediateTerminateNoneDepositModal>
                    }
                    width="284px"
                    confirmStyle={ConfirmStyle}
                    cancelStyle={ConfirmStyle}
                    onClickSubmit={() => {
                        transferToNative("", "closeUnsubscribeView");
                    }}
                    confirmText="확인"
                    useOnCloseView={false}
                />
            )}

            {/* 해지 예약 안내 모달 */}
            <MgDialog
                open={isOpenReservationModal}
                onCloseView={() => setIsOpenReservationModal(false)}
                contents={
                    <ReservationModalContainer>
                        <div className="Title">해지 예약하기</div>
                        <div className="SubTitle">
                            해지 예약하시면, 다음 결제일에 <br />
                            자동 결제가 되지 않습니다 <br />
                            현재 구독 기간 만료까지 <br />
                            서비스 정상 이용 가능합니다.
                        </div>
                    </ReservationModalContainer>
                }
                width="284px"
                confirmStyle={ConfirmStyle}
                cancelStyle={ConfirmStyle}
                confirmText="해지 예약"
                cancelText="취소"
                onClickSubmit={() => {
                    userSubscriptionRemoveReserve.mutate();
                }}
                useOnCloseView={false}
            />

            {/* 인앱으로 인해 해지불가 안내 모달 */}
            {isOpenInAppErrorModal && (
                <MgDialog
                    open={isOpenInAppErrorModal}
                    onCloseView={() => setIsOpenInAppErrorModal(false)}
                    contents={
                        <ImmediateTerminateNoneDepositModal>
                            <div className="Title">해지불가</div>
                            <div className="SubTitle">인앱으로 결제한 구독 상품으로, 앱스토어에서만 해지 가능합니다.</div>
                        </ImmediateTerminateNoneDepositModal>
                    }
                    width="284px"
                    confirmStyle={ConfirmStyle}
                    cancelStyle={ConfirmStyle}
                    onClickSubmit={() => {
                        getDeviceType() === "ios" && window.open("https://apps.apple.com/account/subscriptions?appId=1637542873");
                    }}
                    confirmText="확인"
                    useOnCloseView={false}
                />
            )}

            {/* 위약금 결제된다는 안내 모달 */}
            {isOpenPayDepositModal && (
                <MgDialog
                    open={isOpenPayDepositModal}
                    onCloseView={() => setIsOpenPayDepositModal(false)}
                    contents={
                        <ImmediateTerminateNoneDepositModal>
                            <div className="Title">정말로 해지하시겠습니까?</div>
                            <div className="SubTitle">
                                결제하신 카드로 <br />
                                안내된 위약금 {subsRefundAmount?.penaltyPrice?.toLocaleString()}원이 <br />
                                자동 인출됩니다.
                            </div>
                        </ImmediateTerminateNoneDepositModal>
                    }
                    width="284px"
                    confirmStyle={PayConfirmStyle}
                    cancelStyle={ConfirmStyle}
                    onClickSubmit={() => {
                        setIsOpenPayDepositModal(false);
                        monthlyUserSubscriptionRemove?.mutate({ penaltyPrice: Number(subsRefundAmount?.penaltyPrice) });
                    }}
                    confirmText="네, 해지할래요"
                    cancelText="취소"
                    useOnCloseView={false}
                />
            )}

            {showErrorModal && (
                <ConfirmModal
                    resultMessage={
                        <>
                            <p css={dialogTitleStyle}>해지 실패</p>
                            <p css={dialogContentStyle}>{errorMessage}</p>
                        </>
                    }
                    onConfirm={() => setShowErrorModal(false)}
                />
            )}
            {showUsedStateLand && (
                <ConfirmModal
                    resultMessage={
                        <>
                            <p css={dialogTitleStyle}>환불불가</p>
                            <p css={dialogContentStyle}>
                                약관에 따라 국•공유지 필터 사용 시<br />
                                구독 환불이 불가합니다
                            </p>
                        </>
                    }
                    onConfirm={() => setShowUsedStateLand(false)}
                />
            )}
        </>
    );
};
export default UnSubscribe;

const ImmediateTerminateNoneDepositModal = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding-bottom: 10px;

    text-align: center;

    .Title {
        color: #0c002c;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 26px;
    }

    .SubTitle {
        color: #0c002c;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px;
    }
`;

const ModalContentStyle = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    .titleText {
        text-align: center;
        font-size: 16px;
        font-weight: 400;
        line-height: 26px;
    }
    .titleDescText {
        margin-bottom: 6px;
    }
    .priceText {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    .usedText {
        padding-bottom: 8px;
        border-bottom: 0.6px solid #8d8d8d;
    }
    .refundText {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-weight: 700;
    }

    .refundWarningText {
        color: #f41039;
        text-align: center;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px;
    }
`;

const ReservationModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding-bottom: 10px;

    text-align: center;

    .Title {
        color: #0c002c;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 26px;
    }

    .SubTitle {
        color: #0c002c;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px;
    }
`;

const LottieBoxStyle = styled.div`
    padding: 80px 0 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    .lottie {
        width: 140px;
        height: 140px;
    }
`;

const ConfirmStyle = css`
    font-size: 18px;
`;

const PayConfirmStyle = css`
    font-size: 18px;
    background-color: #f22 !important;
    color: #fff !important;
    width: fit-content !important;
    white-space: nowrap !important;
`;

const rootBoxStyle = css`
    height: 100dvh;
    height: 100vh;
    overflow: hidden;
`;

const headStyle = css`
    display: flex;
    padding: 14px;
    justify-content: space-between;
    align-items: center;
    color: #0c002c;
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    line-height: 28px;
    height: 56px;
    position: absolute;
    top: 0;
    width: 100vw;
    background-color: #ffffff;
`;

const contentStyle = css`
    height: 100%;
    margin-top: 60px;
`;

const lottieBoxStyle = css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 60vh;
    width: 100%;
    & > .lottie {
        width: 30%;
    }
`;

const dialogTitleStyle = css`
    text-align: center;
    font-size: 16px;
    font-weight: 400;
    line-height: 26px;
`;

const dialogContentStyle = css`
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    margin: 8px 0 18px;
    word-break: break-word;
`;
